import { AgGridVue } from 'ag-grid-vue';
import enums from '@/enums';
import { mapGetters } from 'vuex';

/**
 * Provide common functionality to use AgGrid table
 * @mixin views/modules/mixins/commonAgGrid
 *
 */
export default {
  components: {
    AgGridVue,
  },
  i18n: {
    messages: {
      en: {

      },
    },
  },
  props: {
    agGridOverlayId: {
      type: String,
      required: false,
      default() {
        const random = Math.random().toString(36).substring(7);
        return `agGridOverlay-${random}`;
      },
    },
    hideActionsColumn: {
      type: Boolean,
      default: false,
    },
    hideTimestampsColumns: {
      type: Boolean,
      default: false,
    },
    hideCreatedAtColumn: {
      type: Boolean,
      default: false,
    },
    hideUpdatedAtColumn: {
      type: Boolean,
      default: false,
    },
    createdAtHeaderName: {
      type: String,
      default: '$General.CreatedAt',
    },
    updatedAtHeaderName: {
      type: String,
      default: '$General.UpdatedAt',
    },
    initialFilters: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    initialFilterMatch: {
      type: String,
      required: false,
      default: enums.AppFilterMathType.ALL,
    },
    actionsMinWidth: {
      type: Number,
      required: false,
      default: 150,
    },
    actionsMaxWidth: {
      type: Number,
      required: false,
      default: 400,
    },
    readonlyList: {
      type: Boolean,
      required: false,
      default: false,
    },
    floatingFilter: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      dataModel: this.$enums.DataModel.SERVER,
      count: 0,
      agGridKey: 0,
      afterOnModelUpdateFunction: null,
      selectionPageAlert: false,
      firstGetRows: true,
      filtersAppliedCount: 0,
      maxConcurrentDatasourceRequests: 1,
      cacheBlockSize: 100,
      currentPage: 1,
      maxBlocksInCache: 1,
      defaultPaginationPageSize: 10,
      filtersMatch: this.initialFilterMatch,
      overlayLoadingTemplate: `<div class="ag-overlay-loading-center"
          style="box-shadow: none; background: transparent; width: 100px; height: 100px">
          <div id="${this.agGridOverlayId}"
            class="vs-con-loading__container"
            style="background: transparent; width: 100%; height: 100%">
          </div>
        </div>`,
      noRowsMessage: 'There are no data',
      noRowsOverlayComponent: null,
      noRowsOverlayComponentParams: null,
      dataSource: {
        async getRows(params) {
          const { componentParent } = params.context;
          const overlayId = `#${componentParent.agGridOverlayId}`;

          if (componentParent.setFiltersBulkOperation) {
            componentParent.setFiltersBulkOperationCount += 1;
          }

          if (componentParent.setFiltersBulkOperation
            && componentParent.setFiltersBulkOperationCount
            < componentParent.setFiltersBulkOperationAmount) {
            return;
          }

          if (componentParent.setFiltersBulkOperation) {
            componentParent.setFiltersBulkOperation = false;
            componentParent.setFiltersBulkOperationCount = 0;
            componentParent.setFiltersBulkOperationAmount = 0;
          }

          componentParent.gridApi.showLoadingOverlay();
          componentParent.$vs.loading({
            container: overlayId,
            scale: 0.6,
          });

          // Get agGrid column filters
          const agGridFilters = componentParent.getMappedFilterModel();

          // Merge agGrid filters with any existing segment filters
          let mergedFilters = { ...agGridFilters };
          let filtersMatch = componentParent.filtersMatch;

          // If we have a selected segment, we need to include its filters
          if (componentParent.selectedSegment && componentParent.selectedSegment.filters) {
            const segmentFilters = componentParent.selectedSegment.filters;
            const segmentFiltersMatch = componentParent.selectedSegment.filtersMatch;

            // If we have both agGrid filters and segment filters, merge them properly
            if (Object.keys(agGridFilters).length > 0 && Object.keys(segmentFilters).length > 0) {
              mergedFilters = {
                ...segmentFilters,
                agGridFilters: {
                  filterType: componentParent.$enums.AppFilterType.CONDITIONAL,
                  filtersMatch: componentParent.filtersMatch,
                  filter: agGridFilters,
                },
              };
              filtersMatch = segmentFiltersMatch;
            } else if (Object.keys(segmentFilters).length > 0) {
              // Only segment filters
              mergedFilters = segmentFilters;
              filtersMatch = segmentFiltersMatch;
            }
            // If only agGrid filters, keep the existing logic
          }

          const data = await componentParent.fetchAgGridData({
            sortBy: params.sortModel,
            filters: mergedFilters,
            filtersMatch,
            skip: params.startRow,
            limit: componentParent.cacheBlockSize,
          });

          const rowsThisPage = data.data;
          let lastRow = -1;
          componentParent.count = data.count || 0;
          console.log('data.count', data.count);
          console.log('params.endRow', params.endRow);

          if (data.count) {
            if (data.count <= params.endRow) {
              lastRow = data.count;
            }
          } else if (rowsThisPage.length < componentParent.cacheBlockSize) {
            lastRow = params.endRow - componentParent.cacheBlockSize + rowsThisPage.length;
          }

          params.successCallback(rowsThisPage, lastRow);
          componentParent.$vs.loading.close(`${overlayId} > .con-vs-loading`);

          // eslint-disable-next-line max-len
          // Only hide overlay if we have data, otherwise keep the overlay (will be set to no-rows below)
          if (componentParent.count > 0) {
            componentParent.gridApi.hideOverlay();
          }

          if (componentParent.lastSavedId || componentParent.lastSavedIds.length > 0) {
            const nodes = [];

            if (componentParent.lastSavedId) {
              const node = componentParent.gridApi.getRowNode(componentParent.lastSavedId);
              if (node) {
                nodes.push(node);
              }
            } else {
              componentParent.gridApi.forEachNode((node) => {
                if (componentParent.lastSavedIds.includes(node.id)) {
                  nodes.push(node);
                }
              });
            }
            
            // Only flash cells if we have valid nodes (row might have moved to another tab)
            if (nodes.length > 0 && nodes.every(n => n !== null)) {
              componentParent.gridApi.flashCells({
                rowNodes: nodes,
                flashDelay: 3000,
                fadeDelay: 2000,
              });
            }

            componentParent.lastSavedId = null;
            componentParent.lastSavedIds = [];
          }

          if (componentParent.count === 0) {
            componentParent.gridApi.showNoRowsOverlay();
          }
        },
      },
      previousFiltersBulkOperation: false,
      setFiltersBulkOperation: false,
      setFiltersBulkOperationAmount: 0,
      setFiltersBulkOperationCount: 0,
      columnDefs: [],
      rowsSelected: [],
      selectedAllRows: false,
      getRowNodeId: null,
      searchQuery: '',
      gridApi: null,
      gridColumnApi: null,
      gridOptions: {
        context: {
          componentParent: this,
        },
      },
      columnTypes: {
        dateTimeColumn: {
          minWidth: 260,
          maxWidth: 300,
          filter: 'agDateColumnFilter',
          filterParams: {
            suppressAndOrCondition: true,
            alwaysShowBothConditions: false,
          },
          cellClass: 'flex items-center',
          valueFormatter: (params) => this.$options.filters.date_format(
            params.value,
            'DD MMM YYYY hh:mm a',
            true,
            this.tenantAccountDetailsTimezone,
          ),
        },
        dateColumn: {
          minWidth: 250,
          maxWidth: 250,
          filter: 'agDateColumnFilter',
          filterParams: {
            suppressAndOrCondition: true,
            alwaysShowBothConditions: false,
          },
          cellClass: 'flex items-center',
          valueFormatter: (params) => this.$options.filters.date_format(
            params.value,
            'DD MMM YYYY',
            true,
            this.tenantAccountDetailsTimezone,
          ),
        },
      },
      defaultColDef: {
        floatingFilter: !this.readonlyList && this.floatingFilter,
        sortable: true,
        resizable: true,
        suppressMenu: true,
        unSortIcon: true,
        filterParams: {
          buttons: ['reset', 'apply'],
          closeOnApply: true,
          alwaysShowBothConditions: true,
        },
      },
      commonColumnDefs: [
        {
          colId: 'creator',
          headerName: 'Creator',
          field: 'creator.id',
          filter: false,
          width: 0,
          hide: true,
        },
        {
          colId: 'createdAt',
          headerName: this.$t(this.createdAtHeaderName),
          field: 'createdAt',
          sort: 'desc',
          type: 'dateColumn',
        },
        {
          colId: 'updatedAt',
          headerName: this.$t(this.updatedAtHeaderName),
          field: 'updatedAt',
          type: 'dateColumn',
        },
        {
          colId: 'actions',
          minWidth: this.actionsMinWidth,
          maxWidth: this.actionsMaxWidth,
          sortable: false,
          filter: false,
          pinned: 'right',
          cellRendererFramework: 'CellRendererActions',
        },
      ],
      lastSavedId: null,
      lastSavedIds: [],
      showOnlyFirstColumnOnMobile: true,
      paginationPageSize: 10,
      preferFitColumns: false,
      autoSizeAndFitColumns: true,

      visibleMobileColumns: [],
      visibleTabletColumns: [],
      visibleLaptopColumns: [],
    };
  },
  computed: {
    ...mapGetters({
      tenantAccountDetailsTimezone: 'auth/tenantAccountDetailsTimezone',
    }),
    overlayNoRowsTemplate() {
      return `<span class="ag-grid-no-rows-overlay">${this.noRowsMessage}</span>`;
    },
    totalPages() {
      if (this.gridApi) {
        return this.gridApi.paginationGetTotalPages() || 0;
      }
      return 0;
    },
    domLayout() {
      return 'normal';
    },
    gridStyle() {
      // let height = '80vh';
      let minHeight = '550px';

      if (this.$mq === this.$enums.mqBreakpoints.MOBILE) {
        // height = '60vh';
        minHeight = '60vh';
      }
      // else if (this.count <= 1) {
      //   height = '40vh';
      // } else if (this.count <= 2) {
      //   height = '48vh';
      // } else if (this.count <= 3) {
      //   height = '56vh';
      // } else if (this.count <= 4) {
      //   height = '64vh';
      // } else if (this.count <= 5) {
      //   height = '72vh';
      // }

      return { minHeight };
    },
    hasPagination() {
      return this.$mq !== this.$enums.mqBreakpoints.MOBILE;
    },
    suppressPaginationPanel() {
      return this.$mq === this.$enums.mqBreakpoints.MOBILE;
    },
    suppressColumnVirtualisation() {
      return this.$mq === this.$enums.mqBreakpoints.MOBILE;
    },
    getColumnsToExport() {
      return this.columnDefs.filter(
        (c) => !c.colId || ['creator', 'actions'].indexOf(c.colId) === -1,
      ).map((c) => ({
        title: c.headerName,
        field: c.field,
      }));
    },
    columnDefsToImport() {
      return this.additionalColumnDefsToImport.concat(this.columnDefs.slice(
        0, this.columnDefs.length - this.commonColumnDefs.length,
      ).filter((value) => !value.ignoreOnImport).map((value) => ({
        headerName: value.headerName,
        field: value.field,
        required: value.requireOnImport || false,
        requireIfNull: value.requireOnImportIfNull || null,
        validation: value.validationOnImport || null,
        validateValueOnImport: value.validateValueOnImport || null,
      })));
    },
    additionalColumnDefsToImport() {
      return [];
    },
    rowsSelectedCount() {
      return this.rowsSelected.length;
    },
    rowsSelectedCountWithAllSelection() {
      return this.selectedAllRows ? this.count : this.rowsSelectedCount;
    },
    isAnyRowsSelected() {
      return this.rowsSelectedCount > 0;
    },
    isAnyRowsSelectedOrAll() {
      return this.isAnyRowsSelected || this.selectedAllRows;
    },
    rowsSelectedAreReallyAll() {
      return this.rowsSelectedCount === this.count;
    },
    rowsSelectedWithAllSelectionOrNull() {
      return !this.selectedAllRows || this.rowsSelectedAreReallyAll
        ? this.rowsSelected : null;
    },
  },
  watch: {
    $mq(newVal, oldVal) {
      if ((newVal === this.$enums.mqBreakpoints.MOBILE
        || oldVal === this.$enums.mqBreakpoints.MOBILE)
        && newVal !== oldVal) {
        this.setSelectedRowsNone();
        this.agGridKey += 1;
        this.rowsSelected = [];
      }
    },
    filtersMatch() {
      this.resetDataSourceAndSelection();
      this.$emit('update:initial-filter-match', this.filtersMatch);
    },
  },
  created() {
    this.paginationPageSize = this.defaultPaginationPageSize;
  },
  beforeMount() {
    this.getRowNodeId = (data) => data.id;
    if (this.hideActionsColumn) {
      this.deleteCommonColumnDef('actions');
    }

    if (this.hideCreatedAtColumn || this.hideTimestampsColumns) {
      this.deleteCommonColumnDef('createdAt');
    }

    if (this.hideUpdatedAtColumn || this.hideTimestampsColumns) {
      this.deleteCommonColumnDef('updatedAt');
    }

    this.columnDefs = [...this.columnDefs, ...this.commonColumnDefs];
  },
  beforeDestroy() {
    this.gridApi.destroy();
  },
  methods: {
    async onGridReady(params) {
      this.loadGridApi();

      if (this.dataModel === this.$enums.DataModel.SERVER) {
        const overlayId = `#${this.agGridOverlayId}`;
        this.gridApi.showLoadingOverlay();
        this.$vs.loading({
          container: overlayId,
          scale: 0.6,
        });

        await this.setAgGridDataSource();
      }

      if (typeof this.initialFilters === 'object' && Object.keys(this.initialFilters).length > 0) {
        this.applyFilters(this.initialFilters);
      }
    },
    onModelUpdate(e) {
      this.count = this.count || e.api.getInfiniteRowCount() || 0;
      this.currentPage = e.api.paginationGetCurrentPage() + 1;

      this.fitColumnsOnChanges();

      if (this.selectedAllRows) {
        this.setSelectedRowsCurrentPage();
      }

      if (typeof this.afterOnModelUpdateFunction === 'function') {
        this.afterOnModelUpdateFunction();
      }

      // Check if we need to show the overlay when count becomes 0
      if (this.count === 0) {
        // Longer delay to ensure any pending operations complete, including datasource calls
        setTimeout(() => {
          if (this.gridApi && this.count === 0) {
            // Double-check by also verifying there are no displayed rows
            const displayedRowCount = this.gridApi.getDisplayedRowCount();
            if (displayedRowCount === 0) {
              this.gridApi.showNoRowsOverlay();
            }
          }
        }, 300);
      }

      this.afterOnModelUpdate();
    },
    onFilterChanged() {
      this.filtersAppliedCount = Object.keys(this.gridApi.getFilterModel()).length;
      this.setSelectedRowsNone();
      this.$emit('update:initial-filters', this.getMappedFilterModel());
    },
    onPaginationChanged(e) {
      if (this.gridApi && e.api) {
        this.gridApi.paginationGoToPage(e.api.paginationGetCurrentPage());
        this.currentPage = e.api.paginationGetCurrentPage() + 1;
      }

      if (this.selectedAllRows) {
        this.setSelectedRowsAll();
      }
    },
    onFirstDataRendered(params) {
      this.fitColumnsOnChanges();
    },
    onGridSizeChanged(params) {
      this.fitColumnsOnChanges();
    },
    async setAgGridDataSource() {
      this.gridApi.setDatasource(this.dataSource);
    },
    updateSearchQuery(val) {
      this.gridApi.setQuickFilter(val);
    },
    onSelectionChanged() {
      this.rowsSelected = this.gridApi.getSelectedRows();
      this.afterOnSelectionChanged();

      if (!this.currentPageIsSelected()) {
        this.selectionPageAlert = false;
      }
    },
    afterOnSelectionChanged() {},
    afterOnModelUpdate() {},
    loadGridApi() {
      this.gridApi = this.gridOptions.api;
      this.gridColumnApi = this.gridOptions.columnApi;

      if (this.$vs.rtl) {
        const header = this.$refs.agGridTable.$el.querySelector('.ag-header-container');
        header.style.left = `-${String(Number(header.style.transform.slice(11, -3)) + 9)}px`;
      }
    },
    async fetchAgGridData() {
      return {
        data: [],
        count: 0,
      };
    },
    fitColumnsOnChanges() {
      if (!this.autoSizeAndFitColumns) {
        return;
      }

      if (this.gpMqIsLaptop || this.gpMqIsDesktop) {
        if (this.gpMqIsLaptop
          && this.visibleLaptopColumns
          && Array.isArray(this.visibleLaptopColumns)
          && this.visibleLaptopColumns.length > 0) {
          this.columnDefs.forEach((col) => {
            this.gridColumnApi.setColumnVisible(
              col.colId,
              this.visibleLaptopColumns.includes(col.colId),
            );
          });
        } else {
          this.columnDefs.forEach((col) => {
            this.gridColumnApi.setColumnVisible(col.colId, !col.hide && col.colId !== 'actions');
          });
        }

        this.autoSizeOrFitColumnsByViewport(this.preferFitColumns);
        this.gridColumnApi.setColumnVisible('actions', true);
        this.gridColumnApi.autoSizeColumn('actions', true);
      } else if (this.$mq === this.$enums.mqBreakpoints.TABLET) {
        if (this.visibleTabletColumns
          && Array.isArray(this.visibleTabletColumns)
          && this.visibleTabletColumns.length > 0) {
          this.columnDefs.forEach((col) => {
            this.gridColumnApi.setColumnVisible(
              col.colId,
              this.visibleTabletColumns.includes(col.colId),
            );
          });
        } else {
          this.columnDefs.forEach((col) => {
            this.gridColumnApi.setColumnVisible(
              col.colId,
              !col.hide && !['actions', 'createdAt', 'updatedAt'].includes(col.colId),
            );
          });
        }

        this.autoSizeOrFitColumnsByViewport(this.preferFitColumns);
      } else {
        if (this.visibleMobileColumns
          && Array.isArray(this.visibleMobileColumns)
          && this.visibleMobileColumns.length > 0) {
          this.columnDefs.forEach((col) => {
            this.gridColumnApi.setColumnVisible(
              col.colId,
              this.visibleMobileColumns.includes(col.colId),
            );
          });
        } else {
          this.columnDefs.forEach((col, index) => {
            this.gridColumnApi.setColumnVisible(
              col.colId,
              index === 0 || !this.showOnlyFirstColumnOnMobile,
            );
          });
        }

        this.gridApi.sizeColumnsToFit();
      }
    },
    remainsSpaceInViewport() {
      const $viewport = this.$el.getElementsByClassName('ag-header-viewport').item(0);
      const $container = this.$el.getElementsByClassName('ag-header-container').item(0);

      if ($viewport && $container) {
        return $container.offsetWidth < $viewport.offsetWidth;
      }

      return null;
    },
    autoSizeOrFitColumnsByViewport(preferFitColumns = false) {
      console.log('autoSizeOrFitColumnsByViewport - preferFitColumns', preferFitColumns);

      if (preferFitColumns) {
        this.gridApi.sizeColumnsToFit();
      } else {
        this.autoSizeColumns();

        if (this.remainsSpaceInViewport()) {
          this.gridApi.sizeColumnsToFit();
        }
      }
    },
    autoSizeColumns() {
      const allColumnIds = [];
      this.gridColumnApi.getAllColumns().forEach((column) => {
        allColumnIds.push(column.colId);
      });
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    },
    rowDataUpdated() {
      this.fitColumnsOnChanges();
    },
    async resetDataSourceAndSelection() {
      if (this.dataModel === this.$enums.DataModel.SERVER) {
        await this.setAgGridDataSource();
        this.setSelectedRowsNone();
      }
    },
    getMappedFilterModel() {
      const filters = { ...this.gridApi.getFilterModel() };

      Object.keys(filters).forEach((key) => {
        if (key === 'tags' || filters[key].filterType === this.$enums.AppFilterType.CATEGORIES) {
          filters[key].filter = filters[key].filter.map((value) => (typeof value === 'object' ? value.id : value));
        }
      });

      return filters;
    },
    getSortModel() {
      const colState = this.gridColumnApi.getColumnState();
      return colState.filter((s) => s.sort != null).map((s) => ({
        colId: s.colId,
        sort: s.sort,
      }));
    },
    applyFilters(filters) {
      if (filters && typeof filters === 'object') {
        const filtersToApply = this.getFiltersToApply(filters);
        this.setBulkOperation(filtersToApply);
        this.gridApi.setFilterModel(filtersToApply);
      }
    },
    getFiltersToApply(filters) {
      if (filters && typeof filters === 'object') {
        return Object.keys(filters).filter((key) => key !== '_id'
          && key !== 'id' && typeof filters[key] === 'object').reduce((obj, key) => {
          const filter = filters[key];

          // Convert segment filter format to AG-Grid filter format
          // Segment format: { filter: "value", filterType: "text", type: "equals" }
          // AG-Grid format: { filterType: "text", type: "equals", filter: "value" }
          if (filter.filterType && filter.type && filter.filter !== undefined) {
            let processedFilter = filter.filter;

            // For category filters, extract IDs from objects if needed
            if (filter.filterType === this.$enums.AppFilterType.CATEGORIES && Array.isArray(filter.filter)) {
              processedFilter = filter.filter.map((value) => (typeof value === 'object' ? value.id || value._id : value));
            }

            // For tag filters, extract IDs from objects if needed
            if (filter.filterType === this.$enums.AppFilterType.TAGS && Array.isArray(filter.filter)) {
              processedFilter = filter.filter.map((value) => (typeof value === 'object' ? value.id || value._id : value));
            }

            // eslint-disable-next-line no-param-reassign
            obj[key] = {
              filterType: filter.filterType,
              type: filter.type,
              filter: processedFilter
            };

            // Add any additional properties like filterTo for range filters
            if (filter.filterTo !== undefined) {
              obj[key].filterTo = filter.filterTo;
            }
          } else {
            // Use as-is for other filter types (already in AG-Grid format)
            // eslint-disable-next-line no-param-reassign
            obj[key] = filter;
          }

          return obj;
        }, {});
      }

      return {};
    },
    setBulkOperation(filtersToApply, minFiltersCount = 1) {
      const filtersCount = this.getFiltersBulkOperationAmount(filtersToApply);

      if (filtersCount > minFiltersCount) {
        this.setFiltersBulkOperation = true;
        this.previousFiltersBulkOperation = true;
        this.setFiltersBulkOperationAmount = filtersCount;
        this.setFiltersBulkOperationCount = 0;
      }
    },
    resetFilters() {
      const model = this.gridApi.getFilterModel();

      if (model && typeof model === 'object' && Object.keys(model).length > 0) {
        const filtersCount = this.getFiltersBulkOperationAmount(model);
        if (filtersCount > 1) {
          this.setFiltersBulkOperation = true;
          this.setFiltersBulkOperationAmount = filtersCount;
          this.setFiltersBulkOperationCount = 0;
        }
      }

      this.gridApi.setFilterModel(null);
    },
    getFiltersBulkOperationAmount(filters) {
      let count = 1;
      // eslint-disable-next-line no-restricted-syntax
      for (const value of Object.values(filters)) {
        // eslint-disable-next-line no-restricted-globals
        if (typeof value === 'object'
        && ([
          this.$enums.AppFilterType.TAGS,
          this.$enums.AppFilterType.BOOLEAN,
          this.$enums.AppFilterType.CATEGORIES,
        ].indexOf(value.filterType) !== -1
        || this.previousFiltersBulkOperation)) {
          count += 1;
        }
      }

      return count;
    },
    paginationSetPageSize(size) {
      this.gridApi.paginationSetPageSize(size);
      this.paginationPageSize = size;
      this.selectionPageAlert = false;
    },
    setSelectedRowsAll() {
      this.selectedAllRows = true;
      this.gridApi.forEachNode((node) => {
        node.setSelected(true);
      });
    },
    setSelectedRowsCurrentPage() {
      const currentPage = this.currentPage - 1;
      const startRow = currentPage * this.paginationPageSize;
      const endRow = startRow + this.paginationPageSize;

      this.gridApi.forEachNode((node) => {
        if (node.rowIndex >= startRow && node.rowIndex < endRow) {
          node.setSelected(true);
        } else {
          node.setSelected(false);
        }
      });
    },
    currentPageIsSelected() {
      const currentPage = this.currentPage - 1;
      const startRow = currentPage * this.paginationPageSize;
      const endRow = startRow + this.paginationPageSize;
      let currentPageIsSelected = true;

      this.gridApi.forEachNode((node) => {
        if (node.rowIndex >= startRow && node.rowIndex < endRow) {
          if (!node.isSelected()) {
            currentPageIsSelected = false;
          }
        } else if (node.isSelected()) {
          currentPageIsSelected = false;
        }
      });

      return currentPageIsSelected;
    },
    setSelectedRowsNone() {
      this.selectedAllRows = false;
      this.gridApi.deselectAll();
    },
    selectFromHeader(selection, fromCheckbox = false) {
      switch (selection) {
        case 'all':
          this.setSelectedRowsAll();
          break;
        case 'none':
          this.setSelectedRowsNone();
          break;
        case 'page':
          this.selectedAllRows = false;
          this.gridApi.deselectAll();
          this.setSelectedRowsCurrentPage();
          this.selectionPageAlert = fromCheckbox && this.count > this.paginationPageSize;
          break;

        default:
      }
    },
    deleteColumnDef(coldId = '') {
      const index = this.columnDefs.findIndex((col) => col.colId === coldId);

      if (index !== -1) {
        this.columnDefs.splice(index, 1);
      }
    },
    deleteCommonColumnDef(coldId = '') {
      const index = this.commonColumnDefs.findIndex((col) => col.colId === coldId);

      if (index !== -1) {
        this.commonColumnDefs.splice(index, 1);
      }
    },
    refreshRowNodeDataById(id, data, flash = true) {
      const rowNode = this.gridApi.getRowNode(id);

      // eslint-disable-next-line no-empty
      if (rowNode) {
        rowNode.data = data;
        this.gridApi.redrawRows();

        if (flash) {
          this.gridApi.flashCells({
            rowNodes: [rowNode],
          });
        }
      }
    },
    flashRowById(id) {
      const rowNode = this.gridApi.getRowNode(id);
      console.log('flashRowById', id, rowNode);

      // eslint-disable-next-line no-empty
      if (rowNode) {
        this.gridApi.flashCells({
          rowNodes: [rowNode],
        });
      }
    },
  },
};
